/**
 * DocumentViewer — slide-in right panel for previewing an uploaded
 * knowledge-base document inline.
 *
 * Supports:
 *  - PDF → embedded <iframe>
 *  - Text/MD/CSV/JSON → fetched and rendered as syntax-styled <pre>/table
 *  - Everything else (docx, etc.) → unsupported-for-inline, shows icon + download
 *
 * Mirrors the conversation `AttachmentViewer` pattern, but points at the
 * knowledge-base document content endpoint instead of `/attachments/:id`.
 */
import { useEffect, useState, type JSX } from 'react'
import { Download, FileCode, FileSpreadsheet, FileText, FileType, X } from 'lucide-react'
import { useBlobUrl } from '../../../hooks/useBlobUrl.js'

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type DocumentKind = 'pdf' | 'text' | 'csv' | 'code' | 'unsupported'

function classifyDocument(mimeType: string, filename: string): DocumentKind {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  const mime = mimeType.toLowerCase()

  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (ext === 'csv' || mime === 'text/csv') return 'csv'
  if (['json', 'xml', 'html', 'htm', 'yaml', 'yml'].includes(ext)) return 'code'
  if (mime.startsWith('text/') || ['txt', 'md', 'log'].includes(ext)) return 'text'
  return 'unsupported'
}

function iconForKind(kind: DocumentKind): typeof FileText {
  switch (kind) {
    case 'pdf': return FileType
    case 'csv': return FileSpreadsheet
    case 'code': return FileCode
    case 'text': return FileText
    default: return FileText
  }
}

export interface DocumentViewerDoc {
  id: string
  filename: string
  mime_type: string
  size_bytes: number
}

export interface DocumentViewerProps {
  doc: DocumentViewerDoc | null
  onClose: () => void
}

export default function DocumentViewer({ doc, onClose }: DocumentViewerProps): JSX.Element | null {
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textLoading, setTextLoading] = useState(false)
  const [textError, setTextError] = useState<string | null>(null)

  const kind = doc ? classifyDocument(doc.mime_type, doc.filename) : 'unsupported'
  const contentUrl = doc ? `/api/admin/ai/documents/${doc.id}/content` : null
  const { url: viewUrl } = useBlobUrl(kind === 'pdf' ? contentUrl : null)

  useEffect(() => {
    if (!doc || (kind !== 'text' && kind !== 'csv' && kind !== 'code')) {
      setTextContent(null)
      setTextError(null)
      return
    }
    setTextLoading(true)
    setTextError(null)
    setTextContent(null)
    let cancelled = false
    fetch(`/api/admin/ai/documents/${doc.id}/content`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (!cancelled) {
          setTextContent(text)
          setTextLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTextError(err instanceof Error ? err.message : 'Failed to load')
          setTextLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [doc, kind])

  // Keyboard close
  useEffect(() => {
    if (!doc) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [doc, onClose])

  if (!doc) return null

  const downloadUrl = `/api/admin/ai/documents/${doc.id}/content?download=1`
  const Icon = iconForKind(kind)

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Slide-in panel from right */}
      <div className="animate-slide-in-right fixed right-0 top-0 z-[61] flex h-full w-[60%] min-w-[500px] flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Icon className="h-5 w-5 shrink-0 text-gray-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-800">{doc.filename}</p>
              <p className="text-[11px] text-gray-400">
                {doc.mime_type}
                {doc.size_bytes > 0 && ` · ${formatSize(doc.size_bytes)}`}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <a
              href={downloadUrl}
              download={doc.filename}
              className="flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2a44b8]"
            >
              <Download className="h-3.5 w-3.5 text-white" />
              <span className="text-white">Download</span>
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
              title="Close (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="min-h-0 flex-1 overflow-auto bg-gray-50">
          {kind === 'pdf' && (
            <iframe src={viewUrl ?? ''} title={doc.filename} className="h-full w-full border-0" />
          )}

          {(kind === 'text' || kind === 'csv' || kind === 'code') && (
            <div className="h-full overflow-auto bg-[#1e1e2e] p-4">
              {textLoading && (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-600 border-t-gray-300" />
                  <span className="ml-2 text-sm">Loading…</span>
                </div>
              )}
              {textError && (
                <div className="py-12 text-center text-sm text-red-400">
                  Failed to load: {textError}
                </div>
              )}
              {textContent !== null && !textLoading && !textError && (
                kind === 'csv' ? (
                  <CsvView content={textContent} />
                ) : (
                  <pre className="overflow-auto text-xs leading-relaxed text-gray-200">
                    <code>{textContent}</code>
                  </pre>
                )
              )}
            </div>
          )}

          {kind === 'unsupported' && (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-[3px] bg-gray-100">
                <Icon className="h-10 w-10 text-gray-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">{doc.filename}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {doc.mime_type} · {formatSize(doc.size_bytes)}
                </p>
                <p className="mt-3 text-xs text-gray-400">
                  This file type can&apos;t be previewed inline.
                </p>
              </div>
              <a
                href={downloadUrl}
                download={doc.filename}
                className="flex items-center gap-2 rounded-sm bg-[#3454cf] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2a44b8]"
              >
                <Download className="h-4 w-4" />
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── CSV table renderer ── */
function CsvView({ content }: { content: string }): JSX.Element {
  const rows = content.split('\n').filter((l) => l.trim())
  const parsed = rows.map((row) => parseCsvRow(row))
  const maxCols = Math.max(...parsed.map((r) => r.length), 1)

  return (
    <div className="overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-[#2a2a3e]">
            {Array.from({ length: maxCols }).map((_, i) => (
              <th key={i} className="border border-[#3a3a4e] px-3 py-1.5 text-left font-semibold text-gray-300">
                {parsed[0]?.[i] ?? ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.slice(1).map((row, ri) => (
            <tr key={ri} className="hover:bg-[#2a2a3e]">
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td key={ci} className="border border-[#3a3a4e] px-3 py-1.5 text-gray-200">
                  {row[ci] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function parseCsvRow(row: string): string[] {
  const cells: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < row.length; i++) {
    const ch = row[i]
    if (ch === '"' && row[i + 1] === '"') {
      current += '"'
      i++
    } else if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      cells.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  cells.push(current)
  return cells
}
